import React, { useState, useEffect } from 'react';
import { Cookie, RefreshCw, Plus, CheckCircle, AlertCircle, Clock, Eye, EyeOff, Power, PowerOff, Shield, Activity, TrendingUp, Globe, Key, Lock, Unlock, Tv, ChevronDown, ChevronUp, Star } from 'lucide-react';

const WORKER_URL = "https://livetv.panguplay.workers.dev";

export default function CookiePortal() {
  const [cookies, setCookies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [recommended, setRecommended] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeAddTab, setActiveAddTab] = useState('jiotv'); // 'jiotv' | 'hotstar' | 'zee5' | 'sonyliv'

  // JioTV form
  const [newCookie, setNewCookie] = useState('');
  const [newDomain, setNewDomain] = useState('https://jiotvbpkmob.cdn.jio.com');

  // OTT shared fields
  const [ottUrl, setOttUrl] = useState('');
  const [ottCookie, setOttCookie] = useState('');
  const [ottName, setOttName] = useState('');
  const [ottUserAgent, setOttUserAgent] = useState('');
  const [ottReferer, setOttReferer] = useState('');
  const [ottOrigin, setOttOrigin] = useState('');

  const [adminKey, setAdminKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCookieValues, setShowCookieValues] = useState({});
  const [showAdminKey, setShowAdminKey] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    fetchCookies();
    const interval = setInterval(fetchCookies, 300000);
    return () => clearInterval(interval);
  }, [adminKey]);

  const fetchCookies = async () => {
    setLoading(true);
    setError('');
    try {
      const endpoint = adminKey
        ? `${WORKER_URL}/cookies?key=${encodeURIComponent(adminKey)}`
        : `${WORKER_URL}/validate`;

      const response = await fetch(endpoint);
      const data = await response.json();

      if (response.ok) {
        if (adminKey) {
          setCookies(sortCookiesByQuality(data.cookies || []));
          setSummary(data.summary || null);
          setRecommended(data.recommended || '');
        } else {
          setCookies(sortCookiesByQuality(data.cookies || []));
          setRecommended(data.recommended || '');
          const validCookies = data.cookies?.filter(c => c.valid) || [];
          setSummary({
            total: data.totalCount || 0,
            valid: data.validCount || 0,
            available: validCookies.length,
            usable: validCookies.filter(c => !c.disabled).length,
            disabled: data.cookies?.filter(c => c.disabled)?.length || 0
          });
        }
        setLastUpdate(new Date().toISOString());
      } else {
        setError(data.error || 'Failed to fetch cookies');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sortCookiesByQuality = (cookieList) => {
    return [...cookieList].sort((a, b) => {
      const aUsable = a.valid && a.available && !a.disabled;
      const bUsable = b.valid && b.available && !b.disabled;
      if (aUsable && !bUsable) return -1;
      if (!aUsable && bUsable) return 1;
      const aValidDisabled = a.valid && a.disabled;
      const bValidDisabled = b.valid && b.disabled;
      if (aValidDisabled && !bValidDisabled) return -1;
      if (!aValidDisabled && bValidDisabled) return 1;
      if (aUsable && bUsable) {
        return (b.expiry || 0) - (a.expiry || 0);
      }
      return 0;
    });
  };

  const handleTrigger = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${WORKER_URL}/trigger`);
      const data = await response.json();
      if (response.ok) {
        setSuccess('Cookie fetch triggered! Refreshing in 3 seconds...');
        setTimeout(() => { fetchCookies(); setSuccess(''); }, 3000);
      } else {
        setError(data.error || 'Failed to trigger fetch');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── ADD JIOTV COOKIE ──
  const handleAddCookie = async () => {
    if (!adminKey.trim()) { setError('Admin key is required to add cookies'); return; }
    if (!newCookie.trim()) { setError('Please enter a cookie value'); return; }
    if (!newDomain.trim()) { setError('Please enter the link domain'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const response = await fetch(
        `${WORKER_URL}/add-cookie?__hdnea__=${encodeURIComponent(newCookie.trim())}&domain=${encodeURIComponent(newDomain.trim())}&key=${encodeURIComponent(adminKey)}`
      );
      const data = await response.json();
      if (response.ok) {
        setSuccess('JioTV cookie added successfully!');
        setNewCookie('');
        setNewDomain('https://jiotvbpkmob.cdn.jio.com');
        setShowAddForm(false);
        setTimeout(() => { fetchCookies(); setSuccess(''); }, 2000);
      } else {
        setError(data.error || 'Failed to add cookie');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── ADD HOTSTAR COOKIE ──
  const handleAddHotstarCookie = async () => {
    if (!adminKey.trim()) { setError('Admin key is required'); return; }
    if (!ottUrl.trim() || !ottCookie.trim()) { setError('URL and Cookie are required'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const params = new URLSearchParams({
        url: ottUrl.trim(),
        cookie: ottCookie.trim(),
        key: adminKey,
        ...(ottName && { name: ottName }),
        ...(ottUserAgent && { userAgent: ottUserAgent }),
        ...(ottReferer && { referer: ottReferer }),
        ...(ottOrigin && { origin: ottOrigin }),
      });
      const response = await fetch(`${WORKER_URL}/add-hotstar-cookie?${params}`);
      const data = await response.json();
      if (response.ok) {
        setSuccess(`Hotstar cookie added! Valid for ${data.validFor}`);
        resetOttForm();
        setShowAddForm(false);
        setTimeout(() => { fetchCookies(); setSuccess(''); }, 2000);
      } else {
        setError(data.error || 'Failed to add Hotstar cookie');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── ADD ZEE5 COOKIE ──
  const handleAddZee5Cookie = async () => {
    if (!adminKey.trim()) { setError('Admin key is required'); return; }
    if (!ottUrl.trim() || !ottCookie.trim()) { setError('URL and Cookie are required'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const params = new URLSearchParams({
        url: ottUrl.trim(),
        cookie: ottCookie.trim(),
        key: adminKey,
        ...(ottName && { name: ottName }),
        ...(ottUserAgent && { userAgent: ottUserAgent }),
      });
      const response = await fetch(`${WORKER_URL}/add-zee5-cookie?${params}`);
      const data = await response.json();
      if (response.ok) {
        setSuccess(`Zee5 cookie added! Valid for ${data.validFor}`);
        resetOttForm();
        setShowAddForm(false);
        setTimeout(() => { fetchCookies(); setSuccess(''); }, 2000);
      } else {
        setError(data.error || 'Failed to add Zee5 cookie');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── ADD SONYLIV COOKIE ──
  const handleAddSonyLivCookie = async () => {
    if (!adminKey.trim()) { setError('Admin key is required'); return; }
    if (!ottUrl.trim() || !ottCookie.trim()) { setError('URL and Cookie are required'); return; }

    setLoading(true); setError(''); setSuccess('');
    try {
      const params = new URLSearchParams({
        url: ottUrl.trim(),
        cookie: ottCookie.trim(),
        key: adminKey,
        ...(ottName && { name: ottName }),
      });
      const response = await fetch(`${WORKER_URL}/add-sonyliv-cookie?${params}`);
      const data = await response.json();
      if (response.ok) {
        setSuccess(`SonyLiv cookie added! Valid for ${data.validFor}`);
        resetOttForm();
        setShowAddForm(false);
        setTimeout(() => { fetchCookies(); setSuccess(''); }, 2000);
      } else {
        setError(data.error || 'Failed to add SonyLiv cookie');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetOttForm = () => {
    setOttUrl(''); setOttCookie(''); setOttName('');
    setOttUserAgent(''); setOttReferer(''); setOttOrigin('');
  };

  const handleToggleCookie = async (source, isCurrentlyDisabled) => {
    if (!adminKey) { setError('Admin key required to enable/disable cookies'); return; }
    setLoading(true); setError(''); setSuccess('');
    try {
      const action = isCurrentlyDisabled ? 'enable' : 'disable';
      const response = await fetch(`${WORKER_URL}/${action}-cookie?source=${source}&key=${encodeURIComponent(adminKey)}`);
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        setTimeout(() => { fetchCookies(); setSuccess(''); }, 1500);
      } else {
        setError(data.error || `Failed to ${action} cookie`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleShowCookie = (source) => {
    setShowCookieValues(prev => ({ ...prev, [source]: !prev[source] }));
  };

  const getStatusBadge = (cookie) => {
    if (!cookie.available && !cookie.valid) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-500/20 text-gray-300 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Unavailable
        </span>
      );
    }
    if (cookie.disabled) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-500/20 text-orange-300 flex items-center gap-1">
          <PowerOff className="w-3 h-3" /> Disabled
        </span>
      );
    }
    if (!cookie.valid) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-300 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Expired
        </span>
      );
    }
    if (cookie.validFor) {
      const hours = parseInt(cookie.validFor);
      if (hours < 2) {
        return (
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-300 flex items-center gap-1">
            <Clock className="w-3 h-3" /> Expiring Soon
          </span>
        );
      }
    }
    if (cookie.usableByPlaylists) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-300 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> Active
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 flex items-center gap-1">
        <Shield className="w-3 h-3" /> Valid
      </span>
    );
  };

  const getSourceColor = (source) => {
    const map = {
      ziotv: 'from-blue-500 to-cyan-500',
      jiotv: 'from-purple-500 to-pink-500',
      sports24: 'from-orange-500 to-red-500',
      cloudplay: 'from-green-500 to-emerald-500',
      allinonereborn: 'from-yellow-500 to-amber-500',
      billatv: 'from-rose-500 to-pink-500',
      manual: 'from-indigo-500 to-blue-500',
      zee5: 'from-violet-500 to-purple-500',
      sonyliv: 'from-sky-500 to-blue-500',
      hotstar: 'from-teal-500 to-cyan-500',
      sunnxt: 'from-orange-400 to-yellow-500',
    };
    if (map[source]) return map[source];
    const colors = ['from-blue-500 to-cyan-500','from-purple-500 to-pink-500','from-orange-500 to-red-500','from-green-500 to-emerald-500'];
    const hash = source.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const isOttSource = (source) => ['zee5', 'sonyliv', 'hotstar', 'sunnxt'].includes(source);

  const formatTime = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', dateStyle: 'short', timeStyle: 'short' });
  };

  const formatExpiredTime = (expiredAgo) => {
    if (!expiredAgo) return 'N/A';
    const match = expiredAgo.match(/(\d+)h\s*(\d+)m/);
    if (!match) return expiredAgo;
    const totalHours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    if (totalHours < 24) return `${totalHours}h ${minutes}m ago`;
    if (totalHours < 168) { const d = Math.floor(totalHours/24); return `${d} day${d>1?'s':''} ${totalHours%24}h ago`; }
    const weeks = Math.floor(totalHours/168);
    return `${weeks} week${weeks>1?'s':''} ago`;
  };

  const tabConfig = [
    { key: 'jiotv', label: 'JioTV', color: 'from-blue-500 to-cyan-500', icon: '📡' },
    { key: 'hotstar', label: 'Hotstar', color: 'from-teal-500 to-cyan-500', icon: '⭐' },
    { key: 'zee5', label: 'Zee5', color: 'from-violet-500 to-purple-500', icon: '🎬' },
    { key: 'sonyliv', label: 'SonyLiv', color: 'from-sky-500 to-blue-500', icon: '📺' },
  ];

  const handleSubmitOtt = () => {
    if (activeAddTab === 'hotstar') return handleAddHotstarCookie();
    if (activeAddTab === 'zee5') return handleAddZee5Cookie();
    if (activeAddTab === 'sonyliv') return handleAddSonyLivCookie();
    return handleAddCookie();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-black/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Cookie className="w-7 h-7" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Cookie Manager Pro
                </h1>
                <p className="text-xs text-blue-300">Advanced multi-source cookie monitoring</p>
              </div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => { setShowAddForm(!showAddForm); setError(''); setSuccess(''); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 transition-all font-semibold text-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Cookie</span>
              </button>
              <button
                onClick={handleTrigger}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 transition-all font-semibold disabled:opacity-50 text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh All</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-7xl">

        {/* Admin Key */}
        <div className="mb-6 bg-white/5 border border-purple-500/30 rounded-2xl p-6 backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-4">
            <Key className="w-5 h-5 text-purple-400" />
            <h2 className="text-xl font-bold">Admin Authentication</h2>
          </div>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type={showAdminKey ? "text" : "password"}
                placeholder="Enter admin key for full control..."
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-purple-500/30 text-white placeholder-purple-300 focus:border-purple-400 focus:outline-none transition-all"
              />
              <button onClick={() => setShowAdminKey(!showAdminKey)} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded">
                {showAdminKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={fetchCookies}
              disabled={loading || !adminKey}
              className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-500 transition-all font-semibold disabled:opacity-50"
            >
              {adminKey ? <Unlock className="w-5 h-5" /> : <Lock className="w-5 h-5" />}
            </button>
          </div>
          {!adminKey && (
            <p className="text-xs text-purple-300 mt-2">
              💡 Admin key required to add/manage cookies and view full cookie values
            </p>
          )}
          {adminKey && (
            <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Authenticated — full admin controls enabled
            </p>
          )}
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-300 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-500/20 border border-green-500/50 text-green-300 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>{success}</div>
          </div>
        )}

        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            {[
              { label: 'Total', value: summary.total, color: 'blue', icon: <Activity className="w-4 h-4" /> },
              { label: 'Available', value: summary.available, color: 'green', icon: <CheckCircle className="w-4 h-4" /> },
              { label: 'Valid', value: summary.valid, color: 'emerald', icon: <Shield className="w-4 h-4" /> },
              { label: 'Usable', value: summary.usable, color: 'cyan', icon: <TrendingUp className="w-4 h-4" /> },
              { label: 'Disabled', value: summary.disabled || 0, color: 'orange', icon: <PowerOff className="w-4 h-4" /> },
            ].map(({ label, value, color, icon }) => (
              <div key={label} className={`bg-white/5 border border-${color}-500/30 rounded-xl p-4 backdrop-blur-sm`}>
                <div className={`flex items-center gap-2 text-${color}-400 mb-2`}>{icon}<span className="text-xs font-semibold">{label}</span></div>
                <div className="text-2xl font-bold">{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Recommended */}
        {recommended && recommended !== 'none' && (
          <div className="mb-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/50 rounded-xl p-4 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-green-300">
              <TrendingUp className="w-5 h-5" />
              <span className="font-semibold">Recommended Source:</span>
              <span className="px-3 py-1 rounded-full bg-green-500/30 font-bold capitalize">{recommended}</span>
            </div>
          </div>
        )}

        {/* ── ADD COOKIE FORM ── */}
        {showAddForm && (
          <div className="mb-6 bg-white/5 border border-green-500/30 rounded-2xl p-6 backdrop-blur-sm">
            <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Add Cookie
            </h2>
            <p className="text-xs text-green-400 mb-5">Admin key required for all cookie operations</p>

            {/* Platform Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {tabConfig.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => { setActiveAddTab(tab.key); setError(''); resetOttForm(); setNewCookie(''); setNewDomain('https://jiotvbpkmob.cdn.jio.com'); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                    activeAddTab === tab.key
                      ? `bg-gradient-to-r ${tab.color} text-white shadow-lg`
                      : 'bg-white/10 hover:bg-white/20 text-blue-200'
                  }`}
                >
                  <span>{tab.icon}</span> {tab.label}
                </button>
              ))}
            </div>

            {/* JioTV Form */}
            {activeAddTab === 'jiotv' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Cookie Value <span className="text-blue-400 font-normal">(without __hdnea__=)</span></label>
                  <input
                    type="text"
                    placeholder="st=...~exp=...~acl=...~hmac=..."
                    value={newCookie}
                    onChange={(e) => setNewCookie(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-green-500/30 text-white placeholder-green-400/50 focus:border-green-400 focus:outline-none transition-all font-mono text-sm"
                  />
                  <p className="text-xs text-green-400 mt-1">Example: st=1764907406~exp=1764993806~acl=/*~hmac=abc123...</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Link Domain</label>
                  <input
                    type="text"
                    placeholder="https://jiotvbpkmob.cdn.jio.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-green-500/30 text-white placeholder-green-400/50 focus:border-green-400 focus:outline-none transition-all"
                  />
                  <p className="text-xs text-green-400 mt-1">Common: https://jiotvbpkmob.cdn.jio.com or https://jiotvpllive.cdn.jio.com</p>
                </div>
              </div>
            )}

            {/* Hotstar Form */}
            {activeAddTab === 'hotstar' && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Stream URL <span className="text-red-400">*</span></label>
                    <input type="text" placeholder="https://jcevents.hotstar.com/..." value={ottUrl} onChange={(e) => setOttUrl(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-teal-500/30 text-white placeholder-teal-400/50 focus:border-teal-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Cookie <span className="text-red-400">*</span></label>
                    <input type="text" placeholder="hdntl=exp=...~acl=...~hmac=..." value={ottCookie} onChange={(e) => setOttCookie(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-teal-500/30 text-white placeholder-teal-400/50 focus:border-teal-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Channel Name <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="Star Sports 1" value={ottName} onChange={(e) => setOttName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-teal-500/30 text-white placeholder-teal-400/50 focus:border-teal-400 focus:outline-none transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">User-Agent <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="Hotstar;in.startv.hotstar/25.12.19.0 (Android/13)" value={ottUserAgent} onChange={(e) => setOttUserAgent(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-teal-500/30 text-white placeholder-teal-400/50 focus:border-teal-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Referer <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="https://www.hotstar.com/" value={ottReferer} onChange={(e) => setOttReferer(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-teal-500/30 text-white placeholder-teal-400/50 focus:border-teal-400 focus:outline-none transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Origin <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="https://www.hotstar.com" value={ottOrigin} onChange={(e) => setOttOrigin(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-teal-500/30 text-white placeholder-teal-400/50 focus:border-teal-400 focus:outline-none transition-all" />
                  </div>
                </div>
                <p className="text-xs text-teal-400">⭐ User-Agent defaults to the working Hotstar Android UA if not specified</p>
              </div>
            )}

            {/* Zee5 Form */}
            {activeAddTab === 'zee5' && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Stream URL <span className="text-red-400">*</span></label>
                    <input type="text" placeholder="https://z5ams.akamaized.net/..." value={ottUrl} onChange={(e) => setOttUrl(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-violet-500/30 text-white placeholder-violet-400/50 focus:border-violet-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Cookie <span className="text-red-400">*</span></label>
                    <input type="text" placeholder="hdnea=exp=...~acl=...~hmac=..." value={ottCookie} onChange={(e) => setOttCookie(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-violet-500/30 text-white placeholder-violet-400/50 focus:border-violet-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Channel Name <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="Zee Tamil" value={ottName} onChange={(e) => setOttName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-violet-500/30 text-white placeholder-violet-400/50 focus:border-violet-400 focus:outline-none transition-all" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">User-Agent <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="Mozilla/5.0 (Windows NT 10.0; Win64; x64)..." value={ottUserAgent} onChange={(e) => setOttUserAgent(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-violet-500/30 text-white placeholder-violet-400/50 focus:border-violet-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                </div>
                <p className="text-xs text-violet-400">🎬 Cookie will be appended as query parameter to the stream URL</p>
              </div>
            )}

            {/* SonyLiv Form */}
            {activeAddTab === 'sonyliv' && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Stream URL <span className="text-red-400">*</span></label>
                    <input type="text" placeholder="https://sony.akamaized.net/..." value={ottUrl} onChange={(e) => setOttUrl(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-sky-500/30 text-white placeholder-sky-400/50 focus:border-sky-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Cookie <span className="text-red-400">*</span></label>
                    <input type="text" placeholder="hdnea=exp=...~acl=...~hmac=..." value={ottCookie} onChange={(e) => setOttCookie(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-sky-500/30 text-white placeholder-sky-400/50 focus:border-sky-400 focus:outline-none transition-all font-mono text-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2">Channel Name <span className="text-blue-400 font-normal">(optional)</span></label>
                    <input type="text" placeholder="Sony Max" value={ottName} onChange={(e) => setOttName(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/10 border border-sky-500/30 text-white placeholder-sky-400/50 focus:border-sky-400 focus:outline-none transition-all" />
                  </div>
                </div>
                <p className="text-xs text-sky-400">📺 SonyLiv uses cookie-in-URL format, no extra headers needed</p>
              </div>
            )}

            {/* Form Buttons */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => { setShowAddForm(false); setError(''); resetOttForm(); setNewCookie(''); setNewDomain('https://jiotvbpkmob.cdn.jio.com'); }}
                className="flex-1 py-3 rounded-xl border-2 border-green-500 hover:bg-green-500/20 transition-all font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitOtt}
                disabled={loading || !adminKey}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 hover:shadow-lg hover:shadow-green-500/50 font-semibold transition-all disabled:opacity-50"
              >
                {loading ? 'Adding...' : !adminKey ? '🔒 Admin Key Required' : `Add ${tabConfig.find(t => t.key === activeAddTab)?.label} Cookie`}
              </button>
            </div>
          </div>
        )}

        {/* Cookie Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {cookies.map((cookie) => (
            <div
              key={cookie.source}
              className={`bg-white/5 border rounded-2xl p-6 backdrop-blur-sm hover:border-blue-400/50 transition-all ${
                cookie.disabled ? 'border-orange-500/30 opacity-75' :
                !cookie.available ? 'border-gray-500/30 opacity-60' :
                isOttSource(cookie.source) ? 'border-purple-500/30' :
                'border-blue-500/30'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getSourceColor(cookie.source)} flex items-center justify-center`}>
                    {isOttSource(cookie.source) ? <Tv className="w-5 h-5" /> : <Cookie className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-lg capitalize flex items-center gap-2">
                      {cookie.source}
                      {isOttSource(cookie.source) && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/30 text-purple-300">OTT</span>
                      )}
                      {recommended === cookie.source && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/30 text-green-300">BEST</span>
                      )}
                    </h3>
                    {cookie.fetchedAtIST && (
                      <p className="text-xs text-blue-400">Fetched: {cookie.fetchedAtIST}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(cookie)}
                  {adminKey && cookie.available && cookie.source !== 'manual' && !isOttSource(cookie.source) && (
                    <button
                      onClick={() => handleToggleCookie(cookie.source, cookie.disabled)}
                      disabled={loading}
                      className={`p-2 rounded-lg transition-all ${cookie.disabled ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400' : 'bg-orange-500/20 hover:bg-orange-500/30 text-orange-400'} disabled:opacity-50`}
                      title={cookie.disabled ? 'Enable Cookie' : 'Disable Cookie'}
                    >
                      {cookie.disabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>

              {cookie.available && cookie.valid ? (
                <div className="space-y-3">
                  {/* Validity */}
                  <div className="p-3 rounded-lg bg-white/5 border border-blue-500/20">
                    <div className="text-xs text-blue-300 mb-1">Validity Status</div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-bold text-green-300">{cookie.validFor || cookie.expiresIn || 'Active'}</div>
                        <div className="text-xs text-blue-400">{cookie.expiryIST}</div>
                      </div>
                      {cookie.usableByPlaylists !== undefined && (
                        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${cookie.usableByPlaylists ? 'bg-cyan-500/20 text-cyan-300' : 'bg-gray-500/20 text-gray-300'}`}>
                          {cookie.usableByPlaylists ? 'Playlist Ready' : 'Not Usable'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* OTT Channel Count */}
                  {isOttSource(cookie.source) && cookie.channelCount !== undefined && (
                    <div className="p-3 rounded-lg bg-white/5 border border-purple-500/20">
                      <div className="flex items-center gap-2 text-xs text-purple-300 mb-1">
                        <Tv className="w-3 h-3" />
                        <span className="font-semibold">Channels</span>
                      </div>
                      <div className="text-sm text-purple-200 font-semibold">{cookie.channelCount} channel cookies cached</div>
                    </div>
                  )}

                  {/* Link Domain (JioTV only) */}
                  {cookie.linkDomain && (
                    <div className="p-3 rounded-lg bg-white/5 border border-purple-500/20">
                      <div className="flex items-center gap-2 text-xs text-purple-300 mb-1">
                        <Globe className="w-3 h-3" />
                        <span className="font-semibold">Link Domain</span>
                      </div>
                      <div className="text-sm text-purple-200 font-mono break-all">{cookie.linkDomain}</div>
                    </div>
                  )}

                  {/* Cookie Value (JioTV sources only, admin) */}
                  {cookie.cookie && adminKey && !isOttSource(cookie.source) && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs text-blue-300 font-semibold flex items-center gap-2">
                          <Key className="w-3 h-3" /> Cookie Value
                        </div>
                        <button onClick={() => toggleShowCookie(cookie.source)} className="p-1 hover:bg-white/10 rounded transition-all">
                          {showCookieValues[cookie.source] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <div className="p-3 rounded-lg bg-black/30 border border-blue-500/20 font-mono text-xs break-all">
                        {showCookieValues[cookie.source] ? cookie.cookie : '•'.repeat(50)}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                  <div className="font-semibold mb-1 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {cookie.expiredAgo
                      ? `Expired ${formatExpiredTime(cookie.expiredAgo)}`
                      : cookie.reason
                        ? (cookie.reason.includes('Expired') && cookie.reason.match(/(\d+)h\s*(\d+)m/)
                          ? `Expired ${formatExpiredTime(cookie.reason.match(/(\d+)h\s*(\d+)m/)[0])}`
                          : cookie.reason)
                        : 'Unavailable'}
                  </div>
                  {cookie.expiryIST && (
                    <div className="text-xs text-red-400 mt-1">
                      {cookie.expiredAgo && `Expired ${formatExpiredTime(cookie.expiredAgo)} • `}
                      Last valid: {cookie.expiryIST}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
          <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" /> Cookie Sources & Management
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 rounded-lg bg-white/5 text-xs text-blue-300">
              <strong className="text-blue-200">Auto-Update:</strong> Cookies are automatically fetched every 4 hours. The system preserves cookies with longer validity.
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-xs text-blue-300">
              <strong className="text-blue-200">Playlist Priority:</strong> Only "usable" cookies (enabled + valid) are used. The cookie with the longest validity is automatically selected.
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-xs text-blue-300">
              <strong className="text-blue-200">Admin Controls:</strong> Enter admin key to add cookies, enable/disable sources, and view full cookie values.
            </div>
            <div className="p-3 rounded-lg bg-white/5 text-xs text-blue-300">
              <strong className="text-blue-200">Last Update:</strong> {lastUpdate ? formatTime(lastUpdate) : 'Not available'}
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-white/10 mt-20 py-8 text-center text-blue-300 text-sm">
        <p>© 2025 PanguPlay Cookie Manager Pro</p>
        <p className="mt-2">Advanced automated cookie management for uninterrupted streaming</p>
      </footer>
    </div>
  );
}